import React, { Fragment } from 'react';

import { buildUpgradeUrl } from '@zapier/checkout';
import { useLink } from '@zapier/common-hooks';
import { Price } from '@zapier/currency';
import { Button, Link } from '@zapier/design-system';

import { Currency, Interval, Plan, TrialType } from '@zapier/upsells/src/types';

import { UpgradeButton } from '@zapier/upsells/src/components/UpgradeModal/UpgradeModalLayout/UpgradeNudge/UpgradeButton';

const monthlyAmount = (amount: number, interval: Interval) =>
  interval === 'year' ? amount / 12 : amount;

const HeldTasksButton = ({ onClick }: { onClick?: () => void }) => {
  const { routeTo } = useLink();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (routeTo) {
      routeTo('/app/zaps/folder/held');
    }
  };

  return (
    <Button color="greyAlt" key="see-held-tasks" onClick={handleClick}>
      See Held Tasks
    </Button>
  );
};

const TaskLimitUpgradeButton = ({
  currentPlanInterval,
  currentPlanName,
  destinationPlanName,
  onClick,
}: {
  currentPlanInterval: Interval;
  currentPlanName: string;
  destinationPlanName: string;
  onClick?: () => void;
}) => {
  const { routeTo } = useLink();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (routeTo) {
      const url = buildUpgradeUrl(
        currentPlanName,
        destinationPlanName,
        currentPlanInterval
      );

      if (url) {
        routeTo(url);
      }
    }
  };

  return <UpgradeButton onClick={handleClick} />;
};

const baseBodyText = (
  <Fragment>
    You've run out of available tasks on your account, and{' '}
    <Link href="/help/manage/tasks/learn-about-tasks-in-zapier" target="_blank">
      your Tasks are held
    </Link>
    .
  </Fragment>
);

type ActiveViewProps = {
  currency: Currency;
  currentPlan: Plan;
  destinationPlan: Plan;
};

/**
 * Note: Upgrade buttons in these views take the user to the upgrade
 * page rather than doing the upgrade inline because these nudges could
 * be used in `web-ssr`, and the payment app doesn't work in `web-ssr`.
 * This also gives the user the chance to change their billing interval,
 * which is common for annual users who need to upgrade to the next
 * task limit just to get them through the rest of the month.
 */
const views = {
  ['LEAVING_FREE']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: ActiveViewProps) => ({
    bodyText: (
      <Fragment>
        {baseBodyText} Upgrade your plan now to get{' '}
        {Number(destinationPlan.tasks).toLocaleString()} monthly tasks for{' '}
        <Price currency={currency}>
          {monthlyAmount(destinationPlan.amount, destinationPlan.interval)}
        </Price>
        /mo.{' '}
      </Fragment>
    ),
    buttons: [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,

      <HeldTasksButton />,
    ],
    type: 'LEAVING_FREE',
  }),

  ['NEW_PLAN_TYPE']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: ActiveViewProps) => ({
    bodyText: currentPlan ? (
      <Fragment>
        {baseBodyText} Upgrade to a Zapier {destinationPlan.type} account now to
        get {Number(destinationPlan.tasks).toLocaleString()} monthly tasks for{' '}
        <Price currency={currency}>
          {monthlyAmount(destinationPlan.amount, destinationPlan.interval) -
            monthlyAmount(currentPlan.amount, currentPlan.interval)}
        </Price>{' '}
        more per month.
      </Fragment>
    ) : undefined,
    buttons: [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,

      <HeldTasksButton />,
    ],
    type: 'NEW_PLAN_TYPE',
  }),

  ['NEW_TASK_LIMIT_SAME_PLAN_TYPE']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: ActiveViewProps) => ({
    bodyText: (
      <Fragment>
        {baseBodyText} Upgrade your plan now to get{' '}
        {Number(destinationPlan.tasks).toLocaleString()} monthly tasks for{' '}
        <Price currency={currency}>
          {monthlyAmount(destinationPlan.amount, destinationPlan.interval) -
            monthlyAmount(currentPlan.amount, currentPlan.interval)}
        </Price>{' '}
        more per month.
      </Fragment>
    ),
    buttons: [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,

      <HeldTasksButton />,
    ],
    type: 'NEW_TASK_LIMIT_SAME_PLAN_TYPE',
  }),
};

type Props = {
  activeTrialType: TrialType | null | undefined;
  currency: Currency;
  currentPlan: Plan;
  destinationPlan: Plan;
};

export const getActiveView = ({
  activeTrialType,
  currency,
  currentPlan,
  destinationPlan,
}: Props) => {
  if (activeTrialType === 'Team' || activeTrialType === 'Company') {
    return views.NEW_PLAN_TYPE({
      currency,
      currentPlan,
      destinationPlan,
    });
  }

  if (activeTrialType === 'Professional' || currentPlan.amount === 0) {
    return views.LEAVING_FREE({
      currency,
      currentPlan,
      destinationPlan,
    });
  }

  if (currentPlan.type === destinationPlan.type) {
    return views.NEW_TASK_LIMIT_SAME_PLAN_TYPE({
      currency,
      currentPlan,
      destinationPlan,
    });
  }

  return views.NEW_PLAN_TYPE({
    currency,
    currentPlan,
    destinationPlan,
  });
};
